import { Box, Container, Paper, Tooltip, Typography } from '@mui/material';
import useTranslate from 'Hooks/useTranslate';
import { DataRow } from 'Theme/components';
import { FormattedDate } from 'react-intl';
import { useParams } from 'react-router';
import { useQuery } from '@apollo/client';
import { GetOrganizationDomains } from 'Apollo/queries/organization/GetOrganizationDomains';
import useNotification from 'Hooks/useNotification';
import OwnersList from 'Components/OwnersList/OwnersList';
import { DomainValidationMode } from 'Apollo/fragments/organization/DomainValidationMode';

interface Props {
  organization: IOrganization;
}

function TabGeneral({organization}: Readonly<Props>) {
  const { organizationCode } = useParams<{ organizationCode: string; }>();
  const ts = useTranslate();
  const { onError } = useNotification();

  // Load organization domains to get count and certification status
  const { data: domainsData } = useQuery<
    GetOrganizationDomainsData,
    GetOrganizationDomainsVars
  >(GetOrganizationDomains, {
    variables: {
      code: organizationCode,
      page: {
        first: 1000,
        offset: 0
      }
    },
    onError
  });

  // Calculate domain statistics.
  // A domain is considered "validated" (managed) when it is either fully certified
  // (Strong) or awaiting confirmation (Weak); only domains with no validation
  // attempt yet (None) are counted as "pending validation".
  const totalDomains = domainsData?.organizationDomains.totalCount || 0;
  const validatedDomains = domainsData?.organizationDomains.edges.filter(
    edge => edge.node.validationMode !== DomainValidationMode.None
  ).length || 0;
  const pendingValidationDomains = totalDomains - validatedDomains;

  // Handle description truncation
  const description = organization.description || '';
  const maxDescriptionLength = 200;
  const isTruncated = description.length > maxDescriptionLength;
  const displayDescription = isTruncated 
    ? description.substring(0, maxDescriptionLength) + '...' 
    : description;

  return (
    <Container>
      <Box mb={3}>
        <Typography variant="h4" color="primary" gutterBottom>
          {ts('page.organizationProfile.organizationTitle')} 
        </Typography>
        
        <Paper
          sx={{
            padding: '1.5em 2em',
            backgroundColor: '#1e3a5f',
            color: 'white',
            mb: 2
          }}
        >
          <Typography variant="h5" gutterBottom>
            {organization.name}
          </Typography>
          <Typography
            variant="body1"
            sx={{ opacity: 0.8 }}
          >
            {ts('common.label.id')} {organization.code}
          </Typography>
        </Paper>
      </Box>

      <Paper sx={{
        marginBottom: '1em',
        '& > div': {
          flexDirection: { xs: 'column', sm: 'row' }
        }
      }}>
        <DataRow label={ts('page.organizationProfile.nameLabel')} divider={true}>
          <Typography>{organization.name}</Typography>
        </DataRow>
        <DataRow label={ts('page.organizationProfile.descriptionLabel')} divider={true}>
          <Tooltip 
            title={isTruncated ? description : ''} 
            arrow 
            placement="bottom-start"
            slotProps={{
              tooltip: {
                sx: {
                  maxWidth: 500
                }
              }
            }}
          >
            <Typography
              sx={{ 
                cursor: isTruncated ? 'help' : 'default'
              }}
            >
              {displayDescription}
            </Typography>
          </Tooltip>
        </DataRow>
        <DataRow label={ts('page.organizationProfile.owner')} divider={true}>
          <OwnersList owners={organization.owners} />
        </DataRow>
        <DataRow label={ts('page.organizationProfile.uniqueIdentifier')} divider={true}>
          <Typography>{organization.code}</Typography>
        </DataRow>
        <DataRow label={ts('page.organizationProfile.creationDate')} divider={true}>
          <Typography>
            <FormattedDate
              value={organization.dateCreation}
            />
          </Typography>
        </DataRow>
        <DataRow 
          label={ts('page.organizationProfile.countDomains')} 
          divider={false}
        >
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            width="100%"
            gap={0.5}
            sx={{ minWidth: 0 }}
          >
            <Typography>{ts('page.organizationProfile.domainsLabel', { count: totalDomains })}</Typography>
            <Typography variant="body2" color="textSecondary">
              {ts('page.organizationProfile.domainsDescription', {
                validatedCount: validatedDomains,
                pendingCount: pendingValidationDomains
              })}
            </Typography>
          </Box>
        </DataRow>
      </Paper>
    </Container>
  );
}

export default TabGeneral;
